import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

type Status = 'idle' | 'sending' | 'sent' | 'error';

const budgets = ['< $5k', '$5k – $15k', '$15k – $50k', '$50k+', 'Not sure yet'];
const projectTypes = [
  'Custom web app',
  'SaaS product',
  'Internal tool',
  'AI integration',
  'API / integrations',
  'Other',
];

const contactJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Nexsol Labs',
  url: 'https://nexsollabs.com/contact',
  mainEntity: {
    '@type': 'Organization',
    '@id': 'https://nexsollabs.com/#organization',
    name: 'Nexsol Labs',
    email: 'sasocvetanoski@nexsollabs.com',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'sasocvetanoski@nexsollabs.com',
      areaServed: ['MK', 'BG', 'RS', 'HR', 'BA'],
      availableLanguage: ['English', 'Macedonian'],
    },
  },
};

const contactBreadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nexsollabs.com/' },
    { '@type': 'ListItem', position: 2, name: 'Contact', item: 'https://nexsollabs.com/contact' },
  ],
};

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      company: String(data.get('company') ?? ''),
      project_type: String(data.get('projectType') ?? ''),
      budget: String(data.get('budget') ?? ''),
      message: String(data.get('message') ?? ''),
    };

    const apiUrl = import.meta.env.VITE_API_URL;

    try {
      const res = await fetch(`${apiUrl}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      form.reset();
      setStatus('sent');
    } catch {
      // Backend unreachable — fall back to opening the visitor's mail client.
      const subject = encodeURIComponent(
        `New project inquiry — ${payload.name || 'Nexsol Labs site'}`,
      );
      const body = encodeURIComponent(
        `Name: ${payload.name}\nCompany: ${payload.company || '—'}\nEmail: ${payload.email}\nProject type: ${payload.project_type}\nBudget: ${payload.budget}\n\nMessage:\n${payload.message}`,
      );
      window.location.href = `mailto:sasocvetanoski@nexsollabs.com?subject=${subject}&body=${body}`;
      setStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="Contact — Custom Software Studio in Skopje | Nexsol Labs"
        description="Tell us about your software project. We respond personally to every inquiry — no sales funnel, no boilerplate. Reply within 24h on business days. Skopje, North Macedonia."
        path="/contact"
        keywords="contact software developer Skopje, custom software inquiry Macedonia, контакт софтверска компанија Скопје, hire software studio North Macedonia"
        jsonLd={[contactJsonLd, contactBreadcrumbJsonLd]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-page relative py-20 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="eyebrow">Contact</p>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-white md:text-5xl">
              Tell us what you're trying to{' '}
              <span className="gradient-text">build.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
              The more context, the better. We respond personally — no funnel, no
              auto-responder, no junior account manager standing in the way.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="container-page pb-20 md:pb-28">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr]">
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={handleSubmit}
            className="card space-y-5"
            noValidate
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Your name" name="name" type="text" required />
              <Field label="Email" name="email" type="email" required />
            </div>
            <Field label="Company (optional)" name="company" type="text" />

            <div className="grid gap-5 md:grid-cols-2">
              <Select label="Project type" name="projectType" options={projectTypes} />
              <Select label="Budget" name="budget" options={budgets} />
            </div>

            <div>
              <label
                htmlFor="message"
                className="mb-1.5 block text-sm font-medium text-white/80"
              >
                What are you trying to build?
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                required
                placeholder="A short description of the project, the problem it solves, and any constraints or deadlines we should know about."
                className="w-full rounded-xl border border-white/10 bg-ink-900/60 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-accent-500/60 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
              />
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-xs leading-relaxed text-white/65">
              <p className="font-medium text-white/80">
                Sharing something sensitive?
              </p>
              <p className="mt-1">
                We'll sign an NDA before the first call. Just mention it in your
                message — no need to send paperwork first.
              </p>
            </div>

            <div className="flex flex-col gap-3 pt-2 md:flex-row md:items-center md:justify-between md:gap-4">
              <p className="text-xs text-white/45">
                We'll only use this to reply. No newsletters, no third parties.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Reply within 24h on business days
                </span>
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="btn-primary w-full justify-center disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === 'sending'
                    ? 'Sending…'
                    : status === 'sent'
                      ? 'Thanks — we got your message'
                      : status === 'error'
                        ? 'Opened your email app instead'
                        : 'Send message'}
                </button>
              </div>
            </div>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-6"
          >
            <div className="card">
              <h3 className="font-display text-lg font-semibold text-white">
                Direct contact
              </h3>
              <p className="mt-2 text-sm text-white/80">Sasho Cvetanoski</p>
              <a
                href="mailto:sasocvetanoski@nexsollabs.com"
                className="mt-1 inline-block break-all text-sm text-accent-400 hover:text-accent-500"
              >
                sasocvetanoski@nexsollabs.com
              </a>
              <p className="mt-3 text-sm leading-relaxed text-white/60">
                You write directly to me. Typical response time: within one business
                day.
              </p>
            </div>
            <div className="card">
              <h3 className="font-display text-lg font-semibold text-white">
                Where we are
              </h3>
              <p className="mt-2 text-sm text-white/70">
                Based in Skopje, North Macedonia.
                <br />
                Working with teams worldwide.
              </p>
            </div>
            <div className="card">
              <h3 className="font-display text-lg font-semibold text-white">
                What happens next
              </h3>
              <ol className="mt-3 space-y-2 text-sm text-white/65">
                <li>1. We read your message ourselves.</li>
                <li>2. If it's a fit, we book a 30-minute intro call.</li>
                <li>3. You get an honest answer — even if that answer is "not us."</li>
              </ol>
            </div>
          </motion.aside>
        </div>
      </section>
    </>
  );
}

function Field({
  label,
  name,
  type,
  required,
}: {
  label: string;
  name: string;
  type: string;
  required?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-medium text-white/80"
      >
        {label}
        {required && <span className="ml-0.5 text-accent-400">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        className="w-full rounded-xl border border-white/10 bg-ink-900/60 px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-accent-500/60 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
      />
    </div>
  );
}

function Select({
  label,
  name,
  options,
}: {
  label: string;
  name: string;
  options: string[];
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-medium text-white/80"
      >
        {label}
      </label>
      <select
        id={name}
        name={name}
        className="w-full rounded-xl border border-white/10 bg-ink-900/60 px-4 py-2.5 text-sm text-white focus:border-accent-500/60 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        defaultValue={options[0]}
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-ink-900 text-white">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
